import {
  format,
  isSameYear,
  isThisYear,
  isToday,
  isYesterday,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
// [SolabsForce] Locale do date-fns acompanhando o idioma da interface.
import { withDateFnsLocale } from 'shared/helpers/solabs/dateFnsLocale';

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  // [SolabsForce] locale do app (nomes de mês/dia no idioma da interface)
  return format(unixTime, dateFormat, withDateFnsLocale());
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  // [SolabsForce] locale do app nas duas formatações
  const messageDate = format(messageTime, dateFormat, withDateFnsLocale());
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a', withDateFnsLocale());
  }
  return messageDate;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  // [SolabsForce] locale do app em todas as formatações
  if (isToday(date)) return format(date, 'h:mm a', withDateFnsLocale());
  if (isYesterday(date)) return yesterdayLabel;
  if (isThisYear(date)) return format(date, 'MMM d', withDateFnsLocale());
  return format(date, 'MMM d, yyyy', withDateFnsLocale());
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  // [SolabsForce] locale do app: "há 15 dias" em vez de "15 days ago"
  return formatDistanceToNow(unixTime, withDateFnsLocale({ addSuffix: true }));
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  // [SolabsForce] locale do app (nomes de mês/dia no idioma da interface)
  return format(unixTime, df, withDateFnsLocale());
};

// [SolabsForce] Versão curta das frases do date-fns em pt (dynamicTime com o
// locale pt/pt-BR): "há 15 dias" vira "15d", "há cerca de 1 hora" vira "1h".
// Minutos saem como "min" e meses como "m" (convenção usual em pt-BR, "1 m" =
// 1 mês); o sufixo "ago" vira o prefixo "há".
const PT_SHORT_UNITS = [
  [/^(\d+) minutos?$/, 'min'],
  [/^(\d+) horas?$/, 'h'],
  [/^(\d+) dias?$/, 'd'],
  [/^(\d+) semanas?$/, 'sem'],
  [/^(\d+) (mês|meses)$/, 'm'],
  [/^(\d+) anos?$/, 'a'],
];

const shortTimestampPt = (time, withAgo) => {
  const match = time.match(/^(há|em) (.+)$/);
  if (!match) return null;
  const distance = match[2].replace(/^(cerca de|mais de|quase) /, '');
  if (/^menos de um (segundo|minuto)$/.test(distance)) return 'agora';
  // Futuro ("em 3 dias") fica por extenso, igual ao "in 3 days" do inglês.
  if (match[1] === 'em') return time;
  const unit = PT_SHORT_UNITS.find(([regex]) => regex.test(distance));
  if (!unit) return time;
  const [regex, suffix] = unit;
  const count = distance.match(regex)[1];
  const short = `${count}${suffix}`;
  return withAgo ? `há ${short}` : short;
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // [SolabsForce] dynamicTime em pt devolve "há 15 dias"; o mapa abaixo só
  // conhece o inglês.
  const ptShort = shortTimestampPt(time, withAgo);
  if (ptShort) return ptShort;

  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
